// Copyright 2020 Drivetrain Hub LLC
// GraphQL request helpers for model data.

import gear from "./gear";
import lib from "./lib";
import eng from "./eng";
import gql from "graphql-tag";

const TYPENAME = {
  MODEL_MASTER: 'GearsModel',
  MODEL_RELEASE: 'GearsModelRelease',
  PART: {
    FRAME: 'PartFrame',
    GEAR_DETAIL: 'PartGearDetail',
    GEAR_CONCEPT: 'PartGearConcept',
    GEAR_PHASING: 'PartGearPhasing',
  },
  CONFIG: 'GearsConfig',
  DUTY_CYCLE: 'DutyCycle',
  LOAD_CASE: 'LoadCase',
  RESULTS_POWERFLOW: 'ResultsPowerflow',

};
const INPUT_TYPE = {
  CREATE_LOAD_CASE: 'LoadCaseInput',
  UPDATE_LOAD_CASE: 'UpdateLoadCaseInput',
};

const ORIGIN = [0, 0, 0];
const EYE = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

// region PARTS

const PartType = {
  GEAR_DETAIL: 'GEAR_DETAIL',
  GEAR_CONCEPT: 'GEAR_CONCEPT',
  GEAR_PHASING: 'GEAR_PHASING',
  FRAME: 'FRAME',
};

const PartViewer = `
  color
  transparent
  visible`;

const Part = `
  Id
  name
  frame {
    ...on Part {
      Id
    }
  }
  parent {
    ...on Part {
      Id
    }
  }
  pos
  rot
  viewer {
    ${PartViewer}
  }`;

// region PART FRAGMENTS

// region OBJ FIELDS
// Part obj fields do not need to match the library data structure.
// Only lib fields pertinent to parts are queried.

const gearDetailFields = `
  ${lib.LibraryMaster}
  ${gear.GearDetail}
  ${eng.finishedToothQueryStr}`;

const gearConceptFields = `
  ${lib.LibraryMaster}
  ${gear.GearConcept}`;

const gearPhasingFields = `
  ${lib.LibraryMaster}
  ${gear.GearPhasing}`;

const frameFields = `
  ${lib.LibraryMaster}
  ${gear.Frame}`;

// endregion

const fragmentPart = `
  ...on Part {
    ${Part}
  }`;

const fragmentPartObj = `
  ...on Part {
    ${Part}
    obj {
      Id
      name
    }
  }`;

const fragmentPartLabel = `
  ...on Part {
    Id
    name
    obj {
      name
    }
  }`;

const fragmentPartFrame = `
  ...on PartFrame {
    obj {
      ${frameFields}
    }
  }`;

const fragmentPartGearDetail = `
  ...on PartGearDetail {
    obj {
      ${gearDetailFields}
    }
  }`;

const fragmentPartGearConcept = `
  ...on PartGearConcept {
    obj {
      ${gearConceptFields}
    }
  }`;

const fragmentPartGearPhasing = `
  ...on PartGearPhasing {
    obj {
      ${gearPhasingFields}
    }
  }`;

const partQueryStr = `
  ${fragmentPart}
  ${fragmentPartFrame}
  ${fragmentPartGearDetail}
  ${fragmentPartGearConcept}
  ${fragmentPartGearPhasing}`;

// endregion

// region PART TYPES

const partFrameQueryStr = `
  ...on PartFrame {
    ${Part}
    obj {
      ${frameFields}
    }
  }`;

const partGearDetailQueryStr = `
  ...on PartGearDetail {
    ${Part}
    obj {
      ${gearDetailFields}
    }
  }`;

const partGearConceptQueryStr = `
  ...on PartGearConcept {
    ${Part}
    obj {
      ${gearConceptFields}
    }
  }`;

const partGearPhasingQueryStr = `
  ...on PartGearPhasing {
    ${Part}
    obj {
      ${gearPhasingFields}
    }
  }`;

function getPartQueryStr(typ) {
  // Get query string based on specified part type, otherwise return query string with fragments for all part types.

  switch (typ) {
    case PartType.FRAME:
      return partFrameQueryStr;
    case PartType.GEAR_DETAIL:
      return partGearDetailQueryStr;
    case PartType.GEAR_CONCEPT:
      return partGearConceptQueryStr;
    case PartType.GEAR_PHASING:
      return partGearPhasingQueryStr;
    default:
      return partQueryStr;
  }
}

function getPartType(libTypename) {
  // Get the associated PartType enum for a given library object __typename.

  switch (libTypename) {
    case gear.TYPENAME.FRAME:
      return PartType.FRAME;
    case gear.TYPENAME.GEAR_DETAIL:
      return PartType.GEAR_DETAIL;
    case gear.TYPENAME.GEAR_CONCEPT:
      return PartType.GEAR_CONCEPT;
    case gear.TYPENAME.GEAR_PHASING:
      return PartType.GEAR_PHASING;
  }
}

// endregion

// region GROUND

const GROUND_ID = "000000000000000000000000";

const GROUND_PART = {
  __typename: "PartFrame",
  Id: GROUND_ID,
  name: "Ground",
  frame: null,
  parent: null,
  pos: ORIGIN,
  rot: EYE,
  obj: {
    __typename: "Frame",
    Id: GROUND_ID,
    geom: {__typename: "FrameGeom", pitchCircleDiameter: 0, useEqualSpacing: false, usePitchCircle: false},
    name: "Ground",
    number: '',
    description: '',
    image: '',
    createdBy: null,
    createdOn: null,
    lastModifiedBy: null,
    lastModifiedOn: null,
    lock: false,
    owner: null,
    shared: [],
  },
  viewer: {
    __typename: "PartViewer",
    color: null,
    transparent: false,
    visible: true,
  },
};

const GROUND_PART_CONN = {Id: GROUND_PART.Id, __typename: GROUND_PART.__typename};

// endregion

// endregion

// region CONNECTIONS

const ConnectionType = {
  CLUTCH: 'CLUTCH',
  RATIO: 'RATIO',
  REVOLUTE: 'REVOLUTE',
  WELD: 'WELD',
  POWERLOAD: 'POWERLOAD',
};

const connQueryStr = `
  Id
  typ
  parts {
    ...on Part {
      Id
      name
    }
  }`;

function cleanConnection(connection) {
  // Mutates connection by changing any null parts to ground.
  // Returns mutated connection object.

  if (!connection.parts[0]) connection.parts[0] = GROUND_PART_CONN;
  if (!connection.parts[1]) connection.parts[1] = GROUND_PART_CONN;

  return connection;
}

// endregion

// region MODELS

const modelReleaseFields = `
  Id
  lastModifiedOn
  lastModifiedBy
  lock
  version
  notes`;

const GearsSettings = `
  referencePart`;  // ObjectId field

const gearsModelQueryStr = `
  ${lib.LibraryMaster}
  ${GearsSettings}
  parts {
    ${partQueryStr}
  }
  connections {
    ${connQueryStr}
  }
  releases {
    ${modelReleaseFields}
  }`;

// endregion

// region ANALYSIS

// region RESULTS

const RunCaseType = {
  DUTY_CYCLE: 'DUTY_CYCLE',
  LOAD_CASE: 'LOAD_CASE',
};

// NOTE: `part` and `connection` are ObjectId fields
const PowerflowPoint = `
  part
  connection
  speed
  torque
  cycles`;

// NOTE: `connection` is ObjectId field
const PowerflowMesh = `
  connection
  cycles
  frequency
  pitchLineVelocity
  forceAxial
  forceRadial
  forceTangential
  forceNormal
  forceLoa`;

const ResultsPowerflow = `
  createdOn
  points {
    ${PowerflowPoint}
  }
  meshes {
    ${PowerflowMesh}
  }
`;

// endregion

// region CASES

const gearsConfigQueryStr = `
  Id
  name
  clutched {
    Id
  }`;

const loadCasePowerloadQueryStr = `
  Id
  speed
  torque
  power
  powerload {
    Id
  }`;

// NOTE: Only data required for viewing feature tree.
const loadCaseTreeQueryStr = `
  Id
  name
  duration
  powerflow {
    ${ResultsPowerflow}
  }`;


// NOTE: Only data required for viewing load case table, excludes any results.
const loadCaseQueryStr = `
  Id
  name
  duration
  config {
    Id
    name
  }
  powerloads {
    ${loadCasePowerloadQueryStr}
  }
`;

// NOTE: All fields must be queried when creating new load cases.
const loadCaseFullQueryStr = `
  ${loadCaseQueryStr}
  powerflow {
    ${ResultsPowerflow}
  }
`;

const dutyCycleQueryStr = `
  Id
  name
  notes`;

// NOTE: Only data required for viewing feature tree.
const dutyCycleTreeQueryStr = `
  ${dutyCycleQueryStr}
  loadCases {
    ${loadCaseTreeQueryStr}
  }`;

// NOTE: Only data required for viewing load case table.
const dutyCycleCasesQueryStr = `
  ${dutyCycleQueryStr}
  loadCases {
    ${loadCaseQueryStr}
  }`;

const dutyCycleFullQueryStr = `
  ${dutyCycleQueryStr}
  loadCases {
    ${loadCaseFullQueryStr}
  }`;

// endregion

// endregion

// region ATTACHMENTS

const Attachment = `
  ...on Attachment {
    Id
    name
    tags
    uploadedOn
    uploadedBy
  }`;

// endregion

// region QUERIES

const getModel = gql`
  query getModel ($Id: ID!) {
    gearsModel (Id: $Id) {
      ${gearsModelQueryStr}
    }
  }`;

const getModelRelease = gql`
  query getModelRelease ($Id: ID!) {
    gearsModel: gearsModelRelease (Id: $Id) {
      ${lib.LibraryMaster}
      ${GearsSettings}
      version
      notes
      master {
        ${lib.LibraryMaster}
        releases {
          ${modelReleaseFields}
        }
      }
      parts {
        ${partQueryStr}
      }
      connections {
        ${connQueryStr}
      }
    }
  }`;

// NOTE: Only data required for viewing table of load cases.
const getDutyCycle = gql`
  query getDutyCycle ($Id: ID!) {
    dutyCycle (Id: $Id) {
      ${dutyCycleCasesQueryStr}
    }
  }`;

// NOTE: Only data required for viewing feature tree.
const getDutyCycles = gql`
  query getDutyCycles ($model: ID!) {
    dutyCycles (model: $model) {
      ${dutyCycleTreeQueryStr}
    }
  }`;

// NOTE: Get all load case data, including results.
const getDutyCyclesFull = gql`
  query getDutyCyclesFull ($model: ID!) {
    dutyCycles (model: $model) {
      ${dutyCycleFullQueryStr}
    }
  }`;

const fragmentDutyCycle = gql`
  fragment dutyCycle on DutyCycle {
    ${dutyCycleQueryStr}
  }`;

const fragmentDutyCycleTree = gql`
  fragment dutyCycleTree on DutyCycle {
    ${dutyCycleTreeQueryStr}
  }`;

const fragmentDutyCycleFull = gql`
  fragment dutyCycleFull on DutyCycle {
    ${dutyCycleFullQueryStr}
  }`;

const fragmentLoadCaseTree = gql`
  fragment loadCaseTree on LoadCase {
    ${loadCaseTreeQueryStr}
  }`;

// endregion

export default {
  TYPENAME: TYPENAME,
  INPUT_TYPE: INPUT_TYPE,
  ORIGIN: ORIGIN,
  EYE: EYE,
  // PARTS
  PartType: PartType,
  PartViewer: PartViewer,
  fragmentPart: fragmentPart,
  fragmentPartObj: fragmentPartObj,
  fragmentPartLabel: fragmentPartLabel,
  partQueryStr: partQueryStr,
  partGearDetailQueryStr: partGearDetailQueryStr,
  partGearConceptQueryStr: partGearConceptQueryStr,
  partGearPhasingQueryStr: partGearPhasingQueryStr,
  partFrameQueryStr: partFrameQueryStr,
  getPartQueryStr: getPartQueryStr,
  getPartType: getPartType,
  GROUND_ID: GROUND_ID,
  GROUND_PART: GROUND_PART,
  GROUND_PART_CONN: GROUND_PART_CONN,
  // CONNECTIONS
  ConnectionType: ConnectionType,
  connQueryStr: connQueryStr,
  cleanConnection: cleanConnection,
  // MODELS
  LibraryMaster: lib.LibraryMaster,
  modelReleaseFields: modelReleaseFields,
  // ANALYSIS / RESULTS
  RunCaseType: RunCaseType,
  ResultsPowerflow: ResultsPowerflow,
  // ANALYSIS / CASES
  gearsConfigQueryStr: gearsConfigQueryStr,
  dutyCycleTreeQueryStr: dutyCycleTreeQueryStr,
  loadCaseQueryStr: loadCaseQueryStr,
  loadCaseFullQueryStr: loadCaseFullQueryStr,
  // ATTACHMENTS
  Attachment: Attachment,
  // QUERIES
  getModel: getModel,
  getModelRelease: getModelRelease,
  getDutyCycle: getDutyCycle,
  getDutyCycles: getDutyCycles,
  getDutyCyclesFull: getDutyCyclesFull,
  fragmentDutyCycle: fragmentDutyCycle,
  fragmentDutyCycleTree: fragmentDutyCycleTree,
  fragmentDutyCycleFull: fragmentDutyCycleFull,
  fragmentLoadCaseTree: fragmentLoadCaseTree,
}
